import type { HeroClass } from './heroes.ts';
import type { UniqueSlot } from './progression.ts';
import type { Resources } from './structures.ts';
import type { NpcId, SagaBattle, SagaLine } from './saga.ts';
import { hoursMs, sec } from './units.ts';

/**
 * The sagas beside the Turtle's (docs/SAGAS.md): each one about one part of the titan, with its own question,
 * played chapter by chapter by the whole group alongside the main saga. Lighter than the Turtle's: losing a
 * fight costs nothing (it is tried again whenever), and what one hero finds in a fight stays for the next
 * (the clue board). Texts in i18n under `side.<saga>.*` (`side.<saga>.<chapter>.<act>.<line>`).
 */

export type SideId = 'faces' | 'horn' | 'burn' | 'rails' | 'well' | 'shore' | 'lid';
export type SideDifficulty = 'light' | 'medium' | 'hard' | 'heroic';

/**
 * A scene: who says what (some lines only after a mark, `if.flag` / `if.notFlag`), and the way on: a single «go», or
 * `choices` put to the group's vote (the scene waits until it closes; the winner's marks are set). `given`: clues it
 * leaves on the board when played.
 */
export interface SideScene {
  kind: 'scene';
  id: string;
  lines: SagaLine[];
  given?: string[];
  choices?: { id: string; set: string[] }[];
  /** With this mark there is nothing to decide: the scene just goes on (what happened elsewhere settled it). */
  unless?: string;
}

/**
 * A fight: a saga battle act on an incursion clearing, plus where it happens (`place`, the renderer's dressing),
 * whether it is night, and the clues lying on the field (ids; a hero standing on one picks it up).
 */
export interface SideBattle extends SagaBattle {
  place?: 'village' | 'woods' | 'tail' | 'grave' | 'glass' | 'camp' | 'trap' | 'tower' | 'mine' | 'halls' | 'anvil' | 'forge' | 'pipes' | 'fair' | 'reef' | 'fin' | 'lighthouse' | 'ribs' | 'neck' | 'post' | 'slope';
  night?: boolean;
  clues?: string[];
  /** Clues the fight leaves on the board without being picked up (by its own events, or `winClues` on a win). */
  given?: string[];
  winClues?: string[];
  /**
   * A rescue: people held in a pit out in the woods; a standing hero who stays at the pit frees one at a time, who
   * follows that hero back to the node. Nightmares near them hurt them; one falling loses the fight.
   */
  rescue?: { people: number; ring: number; hp: number };
  /** The Prestacaras: the boss of this fight copies an absent hero (with `boss` for its life by party size). */
  facetaker?: boolean;
  /**
   * A drive (El Cuerno 1): stone boars to take across the field to a pen. They shy from the heroes (herded from
   * behind) and bolt from nightmares; a won fight has at least `need` in the pen. `ogres` come with the waves: one
   * roaring sets the whole herd running away from it, over whatever is in the way.
   */
  herd?: { boars: number; need: number; ogres: number };
  /**
   * The lash (El Cuerno 2): the titan swings its tail. From `first` on, every `every`, the ground tilts to one side
   * and everything loose slides: heroes (not by a mooring post), nightmares (over the edge, into the sea) and the
   * clan's barrels (rolling over what they meet). The last one is the big one. `posts` and `barrels` around the camp.
   */
  lash?: { first: number; every: number; posts: number; barrels: number };
  /**
   * The hunt (El Cuerno 3): the boss is the Old Uro, which lasts for days. An outing is `out` long; its wounds and the
   * traps laid stay for the next one, whoever plays it (SideState.hunt). It charges whatever moves; a still hero is
   * invisible to it.
   */
  hunt?: { out: number };
  /**
   * Ragna (El Cuerno 4): the boss is the Unsleeping herself, in the clan's graveyard. She doesn't flee and hits at a
   * share of her usual strength; if the group chose the clan's call, she kneels at half her life and it ends there.
   * If she already fell in another fight, there is no Ragna: the nightmares round the graves are held off instead.
   */
  ragna?: true;
  /**
   * The collapse (La Quemadura 1): the glass breaks from one side and the break walks across the field; `people` scouts
   * lie wounded on it, to be lifted and taken to the firm rock on the far side; at least `need` of them. Numbers: SIDE.collapse.
   */
  collapse?: { people: number; need: number };
  /**
   * A plunder (La Quemadura 2), as the incursions' (docs/MISIONES.md): chests lie about the field, Orla's cart waits in
   * the middle, draggers haul back what is left on the ground; the group leaves when it wants, with at least PLUNDER.winLoad loaded.
   */
  plunder?: true;
  /**
   * The old trap (La Quemadura 3): the first Vigil's harpoon engine on the clearing's edge; the boss shrugs off almost
   * everything but its harpoon. Wound at two winches, pulled at its lever. Numbers: SIDE.trap.
   */
  trap?: true;
  /**
   * The blasting (La Quemadura 4): the first Vigil's watchtower is the node, the nightmares come out of `cracks` in the
   * glass around it, and powder barrels from a stack by the tower seal them. Numbers: SIDE.blast.
   */
  blast?: { cracks: number };
  /**
   * The runaway carts (Las vías 1): an escort whose train of mine carts has no brake. It runs on rails in, round the
   * cavern a few times and out, quicker and quicker; bone barricades rise on the rails ahead of it (one standing when
   * the train gets there derails a cart), and at the end the drawbridge is up until its counterweight is broken.
   * Numbers: SIDE.rails.
   */
  rails?: true;
  /**
   * The alarm bells (Las vías 2): Dorn's guard automatons stand in three rooms round the node and strike whatever is
   * nearest, hero or nightmare (heroes can't break them; nightmares can, and go for them). A hero who stays by a room's
   * alarm lever pulls it, and that room's automatons go still. Every lever down wins it; the node falling loses it.
   * Numbers: SIDE.bells.
   */
  bells?: true;
  /**
   * The five strikes (Las vías 3): the Deep Anvil is the node, and it beats: a ring of light closes on it again and
   * again, quicker each time. A hero by the Anvil who strikes it (the hammer command) as the ring closes sends a wave of
   * heat through the hall; off the beat, the blow is lost. Five on the beat win it; the Anvil falling loses it.
   * Numbers: SIDE.anvil.
   */
  anvil?: true;
  /**
   * What comes up (Las vías 4): Bruni, the Unsleeping, in the bastion's forge yard among its towers. He doesn't flee and
   * hits at a share of his usual strength; now and then he marks a tower near him, and one nobody stands by in time
   * turns on the heroes a while. A blow under his right arm (from his right side) lands as a critical. The rails left
   * open bring more with him and Dorn's automatons at half his life; brought down, he comes alone. If he already fell
   * elsewhere, the boss is the biggest of the automatons he turned, with the same rule for the towers. Numbers: SIDE.rising.
   */
  rising?: true;
  /**
   * The ink in the water (El pozo 1): the tower defense the other way round. The nightmares climb out of the well in
   * the middle of the square and go for the houses round its edge; later the mill's stone is pushed onto the well
   * (quicker with two). Sealed, it is won; too many houses burned, lost. Numbers: SIDE.well.
   */
  well?: true;
  /**
   * The Drinker (El pozo 2): a descent whose last hall is the well's vein, with the leech at it. Hit it swollen; when it
   * bursts, the water comes back up the rooms and everyone has to be out in the first one. Numbers: SIDE.drinker.
   */
  drinker?: true;
  /**
   * Fair day (El pozo 3): stalls round the square with their wares (Fenn's cart first to go); draggers haul them to
   * their holes, and heroes take dropped ones back. Ink thieves show only near a lantern. It always runs its time:
   * what is saved says how big the fair is. Numbers: SIDE.fair.
   */
  fair?: true;
  /**
   * The low water (La Orilla 1): one road is a causeway out to the reef, where the great net is caught; work it loose,
   * carry it home before the water comes back over the map from the sea, with the Drowned climbing out. Numbers: SIDE.tide.
   */
  tide?: true;
  /**
   * The fin (La Orilla 2): lanes run in from the sea; every so often one is marked and then a wave sweeps it, taking the
   * nightmares in it and throwing out any hero not holding a post. At night the Drowned come up in a mass. Dawn wins it.
   * Numbers: SIDE.fin.
   */
  fin?: true;
  /**
   * The Maw (La Orilla 3): the boss lies at the water's edge with a light over its mouth that drags every hero who sees it
   * towards it; breaking the stalk puts it out a while. The lighthouse is the node, with braziers the Drowned put out and
   * the heroes light again; each one lit weakens the pull. Numbers: SIDE.maw.
   */
  maw?: true;
  /**
   * The way back from the dragon's bones (La Orilla 4): a descent already behind them, with the sea coming in where they
   * came down; out on the rock past the last room (with the dragon's heart, if the group took it). Numbers: SIDE.flight.
   */
  flight?: true;
  /**
   * The dragon in the water (La Orilla 5): it swims round the fin and surfaces to breathe fire down one of the lanes in
   * from the sea, marked a moment ahead; the Drowned come up in batches. With the dragon's heart taken, one hero carries
   * it and the fire follows them, and the night is longer. Dawn wins it. Numbers: SIDE.breath.
   */
  breath?: true;
  /**
   * The Drowned Dragon (La Orilla 5): the fin's wave again, and the dragon on land; in a lane when the fin comes down
   * and unable to move (stunned, taunted, held, breathing, Yorra's harpoon in it, or after the heart), it takes a third
   * of its life. Yorra's boats harpoon it from the sea. Numbers: SIDE.sunken.
   */
  sunken?: true;
  /**
   * The titan's neck (El Párpado 1): its folds breathe (the descent's doors open on the out-breath and shut on the
   * in-breath, squeezing whoever stands in one back the way they came), and the titan shakes now and then: whoever is
   * walking when it hits falls. Numbers: SIDE.neck.
   */
  neck?: true;
  /**
   * The bell's climb (El Párpado 2): an escort whose caravan is the fallen bell, dragged up on ropes by the heroes beside
   * it (and Ulla's stone boars, if the group did El Cuerno). The nightmares' blows cut its ropes, and every rope cut lets
   * it slide back; all of them cut, it rolls down the slope. Each shake makes it slip back too, unless a hero beside it
   * stands still to brace it. Numbers: SIDE.climb.
   */
  climb?: true;
  /**
   * Hanging the bell (El Párpado 2): three winches round the frame (the node) wind it up as long as a hero stands at
   * them, each one manned adding its turn; none manned, it sinks back. Wound all the way up wins it. Numbers: SIDE.winch.
   */
  winch?: true;
  /**
   * The copies (El Párpado 3): the group's heroes in black glass, one after another, each with the kit of the Unsleeping
   * of its class and its player's face, going after whoever its player's spectre goes after. Each hero hits their own
   * copy twice as hard. Out come the copies still standing (the fighters' first); the ones felled stay felled for the
   * next outing. The boss is the first copy's life. Numbers: SIDE.copies.
   */
  copies?: true;
  /**
   * The night of the bell, at the bastion (El Párpado 4): the fight is on the group's own bastion, its works standing and
   * the other players' spectres at their posts, against the Hollow Shell's Marea, bigger than ever. At dusk the Shell
   * comes with it (and the Ash Witch, if the Turtle didn't redeem her); at dawn, what is left of it, with everything. The
   * allies the group made come one at a time, at the worst moments (NIGHT_ALLIES). Numbers: SIDE.night.
   */
  bastion?: 'dusk' | 'dawn';
  /**
   * The bell at midnight (El Párpado 4): it has to ring with the titan's pulse, a ring of light closing on it as on Las
   * vías' Anvil (the hammer command). A blow on the beat pushes every nightmare round the post back and fills the calm;
   * off the beat, the eyelid trembles (whoever is walking falls). The nightmares climb the neck to cut its rope (the
   * node). The calm full wins it. Numbers: SIDE.toll.
   */
  toll?: true;
  /** A crowd crossing the clearing (the night of lanterns): how many, one every so often, and whose faces the impostors wear. */
  crowd?: { people: number; every: number; speed: number; faces: string[] };
}

export type SideAct = SideScene | SideBattle;

export interface SideChapter {
  id: string;
  /** Opens with the group at this level (the active heroes' average), once the chapter before is done. */
  minGroup: number;
  /**
   * Its nightmares and boss have this much life (bp), as a saga chapter's (SagaChapter.toughBp). Added with the class
   * balance (docs/BALANCE_CLASES.md): skills grow with the level, and these keep every chapter at least as hard
   * (side-faces-* with bots; the drive with a herding bot). The two middle chapters of Caras prestadas take the value of
   * the ones around them (no scenario of their own).
   */
  toughBp?: number;
  /** Opens this long after the chapter before it was done (La Quemadura: a chapter a day). */
  wait?: number;
  acts: SideAct[];
  /**
   * The unique piece it gives everyone who played an act, and the hero's level to take it (a hero below it gets it on
   * reaching it), as a Turtle chapter's (docs/UNICAS.md §1): La Orilla 5's amulet, carved from the Drowned Dragon's bones.
   */
  unique?: UniqueSlot;
  uniqueLevel?: number;
}

export interface SideSaga {
  id: SideId;
  difficulty: SideDifficulty;
  /** Who tells it (their portrait on its card). */
  face: NpcId;
  chapters: SideChapter[];
  /** Chapters written so far: the rest wait (the card says the story goes on soon). */
  built: number;
}

const ln = (id: string, who: SagaLine['who']): SagaLine => ({ id, who });

/** La Quemadura opens a chapter a day: this long after the one before it was done. */
const SIDE_WAIT = hoursMs(20);

export const SIDES: SideSaga[] = [
  {
    id: 'faces',
    difficulty: 'medium',
    face: 'odna',
    built: 4,
    chapters: [
      {
        id: 'back',
        minGroup: 10,
        toughBp: 12000,
        acts: [
          { kind: 'scene', id: 'odna', lines: [ln('l1', 'narrator'), ln('l2', 'odna'), ln('l3', 'odna'), ln('l4', 'brann'), ln('l5', 'brann')] },
          { kind: 'battle', id: 'night', mode: 'hold', land: 'quarry', place: 'village', night: true, budgetBp: 5500, inkBp: 1500, fourBp: 11000, clues: ['walked', 'taken', 'tracks'] },
          { kind: 'scene', id: 'door', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'odna')] },
        ],
      },
      {
        id: 'shadow',
        minGroup: 12,
        toughBp: 12000,
        acts: [
          { kind: 'scene', id: 'order', lines: [ln('l1', 'narrator'), ln('l2', 'odna'), ln('l3', 'odna'), ln('l4', 'brann')] },
          {
            kind: 'battle',
            id: 'lanterns',
            mode: 'hold',
            land: 'quarry',
            place: 'village',
            night: true,
            budgetBp: 3000,
            inkBp: 800,
            fourBp: 11000,
            crowd: { people: 26, every: sec(4), speed: 22, faces: ['ebbe', 'woodcutter', 'daughter'] },
            given: ['ebbeFace', 'missingFaces', 'lisShadow'],
            winClues: ['calAbsent'],
          },
          { kind: 'scene', id: 'paper', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'odna')] },
        ],
      },
      {
        id: 'accuse',
        minGroup: 15,
        toughBp: 12000,
        acts: [
          { kind: 'scene', id: 'fenn', given: ['letter'], lines: [ln('l1', 'narrator'), ln('l2', 'brann'), ln('l3', 'fenn'), ln('l4', 'brann'), ln('l5', 'fenn'), ln('l6', 'fenn')] },
          { kind: 'battle', id: 'pit', mode: 'hold', land: 'forest', place: 'woods', night: true, budgetBp: 6000, inkBp: 1500, fourBp: 11000, rescue: { people: 2, ring: 6, hp: 900 }, given: ['ebbeDead'], winClues: ['farAway'] },
          {
            kind: 'scene',
            id: 'accuse',
            lines: [ln('l1', 'narrator'), ln('l2', 'odna'), ln('l3', 'brann')],
            choices: [
              { id: 'cal', set: ['faces.right', 'faces.cal'] },
              { id: 'lis', set: ['faces.wrong', 'faces.lis'] },
              { id: 'nobody', set: ['faces.wrong', 'faces.nobody'] },
            ],
          },
          {
            kind: 'scene',
            id: 'morning',
            lines: [
              { id: 'cal', who: 'narrator', if: { flag: 'faces.cal' } },
              { id: 'lis', who: 'narrator', if: { flag: 'faces.lis' } },
              { id: 'nobody', who: 'narrator', if: { flag: 'faces.nobody' } },
              { id: 'r1', who: 'narrator', if: { flag: 'faces.right' } },
              { id: 'r2', who: 'odna', if: { flag: 'faces.right' } },
              { id: 'w1', who: 'narrator', if: { flag: 'faces.wrong' } },
              { id: 'w2', who: 'odna', if: { flag: 'faces.wrong' } },
            ],
          },
        ],
      },
      {
        id: 'mada',
        minGroup: 17,
        toughBp: 12000,
        acts: [
          {
            kind: 'scene',
            id: 'mada',
            lines: [
              { id: 'l1', who: 'odna' },
              { id: 'l2', who: 'narrator' },
              { id: 'l3', who: 'mada' },
              { id: 'w1', who: 'mada', if: { flag: 'faces.wrong' } },
              { id: 'l4', who: 'mada' },
            ],
            choices: [
              { id: 'wait', set: ['faces.wait'] },
              { id: 'now', set: ['faces.now'] },
            ],
          },
          { kind: 'battle', id: 'facetaker', mode: 'boss', land: 'quarry', place: 'village', night: true, budgetBp: 4500, inkBp: 2000, boss: { kind: 'prestado', tier: 3, hp: [0, 3800, 6600, 11000, 16000] }, facetaker: true, winClues: ['truth'] },
          {
            kind: 'scene',
            id: 'dawn',
            lines: [
              { id: 'l1', who: 'narrator' },
              { id: 'l2', who: 'narrator' },
              { id: 'w1', who: 'mada', if: { flag: 'faces.wait' } },
              { id: 'l3', who: 'narrator' },
              { id: 'l4', who: 'odna' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'horn',
    difficulty: 'medium',
    face: 'ulla',
    built: 4,
    chapters: [
      {
        id: 'drive',
        minGroup: 7,
        toughBp: 13000,
        acts: [
          { kind: 'scene', id: 'ulla', lines: [ln('l1', 'narrator'), ln('l2', 'brann'), ln('l3', 'ulla'), ln('l4', 'ulla')] },
          { kind: 'battle', id: 'drive', mode: 'hold', land: 'quarry', place: 'tail', budgetBp: 2500, inkBp: 800, fourBp: 11000, herd: { boars: 12, need: 6, ogres: 2 } },
          { kind: 'scene', id: 'pen', lines: [ln('l1', 'narrator'), ln('l2', 'cal'), ln('l3', 'cal'), ln('l4', 'ulla')] },
        ],
      },
      {
        id: 'lash',
        minGroup: 9,
        toughBp: 13000,
        acts: [
          { kind: 'scene', id: 'camp', lines: [ln('l1', 'narrator'), ln('l2', 'ulla'), ln('l3', 'ulla'), ln('l4', 'ulla')] },
          { kind: 'battle', id: 'lash', mode: 'hold', land: 'quarry', place: 'tail', budgetBp: 6500, inkBp: 1200, fourBp: 11000, lash: { first: sec(30), every: sec(40), posts: 5, barrels: 6 } },
          { kind: 'scene', id: 'calm', lines: [ln('l1', 'narrator'), ln('l2', 'ulla'), ln('l3', 'narrator'), ln('l4', 'fenn')] },
        ],
      },
      {
        id: 'hunt',
        minGroup: 11,
        toughBp: 13000,
        acts: [
          { kind: 'scene', id: 'tracks', lines: [ln('l1', 'narrator'), ln('l2', 'ulla'), ln('l3', 'ulla'), ln('l4', 'cal')] },
          { kind: 'battle', id: 'uro', mode: 'boss', land: 'quarry', place: 'tail', budgetBp: 3500, inkBp: 1000, boss: { kind: 'uro', tier: 1, hp: [0, 38000, 63000, 92000, 126000] }, hunt: { out: sec(480) } },
          { kind: 'scene', id: 'spear', lines: [ln('l1', 'narrator'), ln('l2', 'ulla'), ln('l3', 'cal'), ln('l4', 'ulla')] },
        ],
      },
      {
        id: 'alone',
        minGroup: 14,
        toughBp: 13000,
        acts: [
          {
            kind: 'scene',
            id: 'grave',
            lines: [
              { id: 'l1', who: 'narrator' },
              { id: 'l2', who: 'ulla' },
              { id: 'l3', who: 'ulla' },
              { id: 'l4', who: 'brann' },
              { id: 'm1', who: 'brann', if: { flag: 'horn.met' } },
              { id: 'f1', who: 'narrator', if: { flag: 'horn.felled' } },
              { id: 'f2', who: 'ulla', if: { flag: 'horn.felled' } },
              { id: 'l5', who: 'ulla', if: { notFlag: 'horn.felled' } },
            ],
            choices: [
              { id: 'call', set: ['horn.call'] },
              { id: 'fall', set: ['horn.fall'] },
            ],
            unless: 'horn.felled',
          },
          { kind: 'battle', id: 'ragna', mode: 'boss', land: 'quarry', place: 'grave', night: true, budgetBp: 3000, inkBp: 1500, boss: { kind: 'ragna', tier: 3, hp: [0, 4600, 8000, 13200, 19200] }, ragna: true },
          {
            kind: 'scene',
            id: 'home',
            lines: [
              { id: 'c1', who: 'narrator', if: { flag: 'horn.call', notFlag: 'horn.felled' } },
              { id: 'c2', who: 'narrator', if: { flag: 'horn.call', notFlag: 'horn.felled' } },
              { id: 'c3', who: 'ulla', if: { flag: 'horn.call', notFlag: 'horn.felled' } },
              { id: 'k1', who: 'narrator', if: { flag: 'horn.fall', notFlag: 'horn.felled' } },
              { id: 'k2', who: 'ulla', if: { flag: 'horn.fall', notFlag: 'horn.felled' } },
              { id: 'f1', who: 'narrator', if: { flag: 'horn.felled' } },
              { id: 'f2', who: 'ulla', if: { flag: 'horn.felled' } },
              { id: 'l1', who: 'cal' },
              { id: 'l2', who: 'ulla' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'burn',
    difficulty: 'light',
    face: 'orla',
    built: 4,
    chapters: [
      {
        id: 'patrol',
        minGroup: 16,
        acts: [
          { kind: 'scene', id: 'orla', lines: [ln('l1', 'narrator'), ln('l2', 'brann'), ln('l3', 'orla'), ln('l4', 'orla'), ln('l5', 'brann')] },
          { kind: 'battle', id: 'glass', mode: 'hold', land: 'quarry', place: 'glass', budgetBp: 6000, inkBp: 1000, fourBp: 11000, collapse: { people: 6, need: 4 } },
          { kind: 'scene', id: 'rock', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'narrator'), ln('l4', 'orla'), ln('l5', 'orla')] },
        ],
      },
      {
        id: 'camp',
        minGroup: 16,
        wait: SIDE_WAIT,
        acts: [
          { kind: 'scene', id: 'marks', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'orla'), ln('l4', 'brann'), ln('l5', 'orla')] },
          { kind: 'battle', id: 'camp', mode: 'hold', land: 'quarry', place: 'camp', budgetBp: 6000, inkBp: 1000, fourBp: 11000, plunder: true },
          { kind: 'scene', id: 'forge', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'hilde'), ln('l4', 'orla'), ln('l5', 'narrator')] },
        ],
      },
      {
        id: 'trap',
        minGroup: 16,
        wait: SIDE_WAIT,
        acts: [
          { kind: 'scene', id: 'engine', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'hilde'), ln('l4', 'hilde'), ln('l5', 'orla')] },
          { kind: 'battle', id: 'harpoon', mode: 'boss', land: 'quarry', place: 'trap', budgetBp: 3000, inkBp: 1000, boss: { kind: 'colossus', tier: 1, hp: [0, 5200, 8400, 13000, 18000] }, trap: true },
          { kind: 'scene', id: 'diary', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'orla'), ln('l4', 'orla'), ln('l5', 'brann'), ln('l6', 'orla')] },
        ],
      },
      {
        id: 'tower',
        minGroup: 16,
        wait: SIDE_WAIT,
        acts: [
          { kind: 'scene', id: 'smoke', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'hilde'), ln('l4', 'orla')] },
          { kind: 'battle', id: 'cracks', mode: 'hold', land: 'quarry', place: 'tower', budgetBp: 3500, inkBp: 1500, fourBp: 11000, blast: { cracks: 4 } },
          { kind: 'scene', id: 'map', lines: [ln('l1', 'narrator'), ln('l2', 'orla'), ln('l3', 'narrator'), ln('l4', 'orla'), ln('l5', 'orla'), ln('l6', 'brann'), ln('l7', 'orla')] },
        ],
      },
    ],
  },
  {
    id: 'rails',
    difficulty: 'hard',
    face: 'hilde',
    built: 4,
    chapters: [
      {
        id: 'descent',
        minGroup: 21,
        acts: [
          { kind: 'scene', id: 'mouth', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'brann'), ln('l4', 'hilde'), ln('l5', 'hilde')] },
          { kind: 'battle', id: 'run', mode: 'escort', land: 'quarry', place: 'mine', budgetBp: 4500, inkBp: 2000, cartHpBp: 20000, rails: true },
          { kind: 'scene', id: 'cavern', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'narrator'), ln('l4', 'brann'), ln('l5', 'hilde')] },
        ],
      },
      {
        id: 'winder',
        minGroup: 23,
        acts: [
          { kind: 'scene', id: 'halls', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'brann'), ln('l4', 'narrator')] },
          { kind: 'battle', id: 'bells', mode: 'hold', land: 'quarry', place: 'halls', budgetBp: 4000, inkBp: 1500, fourBp: 11000, bells: true },
          {
            kind: 'scene',
            id: 'dorn',
            lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'dorn'), ln('l4', 'narrator'), ln('l5', 'dorn'), ln('l6', 'dorn'), ln('l7', 'hilde'), ln('l8', 'dorn')],
          },
        ],
      },
      {
        id: 'anvil',
        minGroup: 25,
        acts: [
          { kind: 'scene', id: 'cold', lines: [ln('l1', 'narrator'), ln('l2', 'dorn'), ln('l3', 'dorn'), ln('l4', 'hilde'), ln('l5', 'dorn')] },
          { kind: 'battle', id: 'strikes', mode: 'hold', land: 'quarry', place: 'anvil', budgetBp: 5000, inkBp: 2000, fourBp: 11000, anvil: true },
          {
            kind: 'scene',
            id: 'key',
            lines: [ln('l1', 'narrator'), ln('l2', 'dorn'), ln('l3', 'dorn'), ln('l4', 'brann'), ln('l5', 'narrator'), ln('l6', 'dorn'), ln('l7', 'fenn')],
          },
        ],
      },
      {
        id: 'rising',
        minGroup: 28,
        acts: [
          {
            kind: 'scene',
            id: 'dawn',
            lines: [
              { id: 'l1', who: 'narrator' },
              { id: 'l2', who: 'dorn' },
              { id: 'l3', who: 'hilde' },
              { id: 'l4', who: 'brann' },
              { id: 'l5', who: 'hilde' },
              { id: 'm1', who: 'brann', if: { flag: 'rails.met' } },
              { id: 'f1', who: 'narrator', if: { flag: 'rails.felled' } },
              { id: 'f2', who: 'hilde', if: { flag: 'rails.felled' } },
              { id: 'l6', who: 'hilde', if: { notFlag: 'rails.felled' } },
              { id: 'l7', who: 'dorn' },
            ],
            choices: [
              { id: 'close', set: ['rails.closed'] },
              { id: 'open', set: ['rails.open'] },
            ],
          },
          { kind: 'battle', id: 'forge', mode: 'boss', land: 'quarry', place: 'forge', budgetBp: 3000, inkBp: 2000, fourBp: 12000, boss: { kind: 'bruni', tier: 3, hp: [0, 12000, 20000, 33000, 48000] }, rising: true },
          {
            kind: 'scene',
            id: 'ash',
            lines: [
              { id: 'b1', who: 'narrator', if: { notFlag: 'rails.felled' } },
              { id: 'b2', who: 'narrator', if: { notFlag: 'rails.felled' } },
              { id: 'b3', who: 'hilde', if: { notFlag: 'rails.felled' } },
              { id: 'f1', who: 'narrator', if: { flag: 'rails.felled' } },
              { id: 'f2', who: 'hilde', if: { flag: 'rails.felled' } },
              { id: 'c1', who: 'dorn', if: { flag: 'rails.closed' } },
              { id: 'o1', who: 'dorn', if: { flag: 'rails.open' } },
              { id: 'l1', who: 'brann' },
              { id: 'l2', who: 'hilde' },
            ],
          },
        ],
      },
    ],
  },
];

// El pozo goes last in the list (its place feeds the fights' seeds); the screens show the sagas by level.
SIDES.push({
  id: 'well',
  difficulty: 'light',
  face: 'pell',
  built: 3,
  chapters: [
    {
      id: 'ink',
      minGroup: 1,
      acts: [
        { kind: 'scene', id: 'bucket', lines: [ln('l1', 'narrator'), ln('l2', 'brann'), ln('l3', 'narrator'), ln('l4', 'pell'), ln('l5', 'pell')] },
        { kind: 'battle', id: 'square', mode: 'hold', land: 'quarry', place: 'village', night: true, budgetBp: 4000, inkBp: 0, fourBp: 11000, well: true },
        { kind: 'scene', id: 'lid', lines: [ln('l1', 'narrator'), ln('l2', 'pell'), ln('l3', 'narrator'), ln('l4', 'brann')] },
      ],
    },
    {
      id: 'drinker',
      minGroup: 2,
      acts: [
        { kind: 'scene', id: 'stone', lines: [ln('l1', 'narrator'), ln('l2', 'pell'), ln('l3', 'pell'), ln('l4', 'brann'), ln('l5', 'pell')] },
        { kind: 'battle', id: 'vein', mode: 'descent', rooms: 2, place: 'pipes', budgetBp: 3200, inkBp: 0, boss: { kind: 'drinker', tier: 1, hp: [0, 900, 1500, 2400, 3300] }, drinker: true },
        { kind: 'scene', id: 'fountain', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'pell'), ln('l4', 'narrator')] },
      ],
    },
    {
      id: 'fair',
      minGroup: 4,
      acts: [
        {
          kind: 'scene',
          id: 'odna',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'b1', who: 'brann', if: { notFlag: 'well.caravan' } },
            { id: 'l2', who: 'odna' },
            { id: 'f1', who: 'odna', if: { notFlag: 'well.caravan' } },
            { id: 'c1', who: 'odna', if: { flag: 'well.caravan' } },
            { id: 'l3', who: 'pell' },
          ],
        },
        { kind: 'battle', id: 'stalls', mode: 'hold', land: 'quarry', place: 'fair', night: true, budgetBp: 3500, inkBp: 0, fourBp: 11000, fair: true },
        {
          kind: 'scene',
          id: 'fenn',
          lines: [
            { id: 'g1', who: 'narrator', if: { flag: 'pozo.feria.grande' } },
            { id: 'm1', who: 'narrator', if: { flag: 'pozo.feria.mediana' } },
            { id: 's1', who: 'narrator', if: { flag: 'pozo.feria.chica' } },
            { id: 'l1', who: 'brann' },
            { id: 'l2', who: 'fenn' },
            { id: 'l3', who: 'fenn' },
            { id: 'l4', who: 'fenn' },
            { id: 'l5', who: 'pell' },
          ],
        },
      ],
    },
  ],
});

// La Orilla, also after the others in the list for the fights' seeds.
SIDES.push({
  id: 'shore',
  difficulty: 'hard',
  face: 'yorra',
  built: 5,
  chapters: [
    {
      id: 'nets',
      minGroup: 28,
      acts: [
        {
          kind: 'scene',
          id: 'scale',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'fenn' },
            { id: 'l3', who: 'narrator' },
            { id: 'l4', who: 'yorra' },
            { id: 'l5', who: 'yorra' },
            { id: 'l6', who: 'hilde' },
            { id: 'l7', who: 'yorra' },
          ],
        },
        { kind: 'battle', id: 'lowwater', mode: 'hold', land: 'quarry', place: 'reef', budgetBp: 3500, inkBp: 2500, fourBp: 11000, tide: true },
        {
          kind: 'scene',
          id: 'burnt',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'hilde' },
            { id: 'b1', who: 'hilde', if: { flag: 'shore.diary' } },
            { id: 'l3', who: 'yorra' },
          ],
        },
      ],
    },
    {
      id: 'fin',
      minGroup: 31,
      acts: [
        { kind: 'scene', id: 'wave', lines: [ln('l1', 'narrator'), ln('l2', 'yorra'), ln('l3', 'yorra'), ln('l4', 'brann')] },
        { kind: 'battle', id: 'lanes', mode: 'hold', land: 'quarry', place: 'fin', budgetBp: 2000, inkBp: 2500, fourBp: 11000, fin: true },
        { kind: 'scene', id: 'father', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'yorra'), ln('l4', 'narrator')] },
      ],
    },
    {
      id: 'maw',
      minGroup: 34,
      acts: [
        { kind: 'scene', id: 'light', lines: [ln('l1', 'narrator'), ln('l2', 'yorra'), ln('l3', 'yorra'), ln('l4', 'brann')] },
        { kind: 'battle', id: 'maw', mode: 'hold', land: 'quarry', place: 'lighthouse', night: true, budgetBp: 2000, inkBp: 3000, fourBp: 11000, maw: true },
        { kind: 'scene', id: 'boat', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'yorra')] },
      ],
    },
    {
      id: 'bones',
      minGroup: 37,
      // A crypt's nightmares don't grow with the heroes' level the way a clearing's do: these are as tough as the group.
      toughBp: 30000,
      acts: [
        { kind: 'scene', id: 'far', lines: [ln('l1', 'narrator'), ln('l2', 'yorra'), ln('l3', 'yorra'), ln('l4', 'narrator'), ln('l5', 'narrator'), ln('l6', 'brann')] },
        { kind: 'battle', id: 'ribs', mode: 'descent', rooms: 3, place: 'ribs', budgetBp: 30000, inkBp: 8000, fourBp: 9500, bestiary: { skeleton: 2, wraith: 3, gargoyle: 2, troll: 2, bonesinger: 1 } },
        {
          kind: 'scene',
          id: 'heart',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'b1', who: 'narrator', if: { flag: 'shore.diary' } },
            { id: 'b2', who: 'hilde', if: { flag: 'shore.diary' } },
            { id: 'l2', who: 'narrator' },
            { id: 'l3', who: 'hilde' },
            { id: 'l4', who: 'yorra' },
          ],
          choices: [
            { id: 'take', set: ['orilla.corazon'] },
            { id: 'leave', set: ['orilla.mar'] },
          ],
        },
        { kind: 'battle', id: 'back', mode: 'descent', rooms: 3, place: 'ribs', budgetBp: 0, flight: true },
        {
          kind: 'scene',
          id: 'claw',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'narrator' },
            { id: 't1', who: 'hilde', if: { flag: 'orilla.corazon' } },
            { id: 'm1', who: 'yorra', if: { flag: 'orilla.mar' } },
            { id: 'l3', who: 'brann' },
          ],
        },
      ],
    },
    {
      id: 'drowned',
      minGroup: 40,
      // The amulet (docs/UNICAS.md §1): Yorra's stilt-houses carve it from what the dragon's bones left.
      unique: 'amulet',
      uniqueLevel: 40,
      acts: [
        {
          kind: 'scene',
          id: 'wall',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'b1', who: 'brann', if: { flag: 'shore.burned' } },
            { id: 'n1', who: 'brann', if: { notFlag: 'shore.burned' } },
            { id: 't1', who: 'narrator', if: { flag: 'orilla.corazon' } },
            { id: 't2', who: 'hilde', if: { flag: 'orilla.corazon' } },
            { id: 'm1', who: 'yorra', if: { flag: 'orilla.mar' } },
          ],
        },
        { kind: 'battle', id: 'breath', mode: 'hold', land: 'quarry', place: 'fin', night: true, budgetBp: 2000, inkBp: 3000, fourBp: 11000, breath: true },
        { kind: 'scene', id: 'boats', lines: [ln('l1', 'narrator'), ln('l2', 'yorra'), ln('l3', 'yorra')] },
        { kind: 'battle', id: 'sunken', mode: 'hold', land: 'quarry', place: 'fin', budgetBp: 1500, inkBp: 3000, fourBp: 11000, sunken: true },
        {
          kind: 'scene',
          id: 'still',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'narrator' },
            { id: 't1', who: 'hilde', if: { flag: 'orilla.corazon' } },
            { id: 'l3', who: 'yorra' },
            // The amulets in Yorra's hands (docs/UNICAS.md §5).
            { id: 'u1', who: 'yorra' },
            { id: 'l4', who: 'brann' },
          ],
        },
      ],
    },
  ],
});

// El Párpado, the last: the titan's head (docs/SAGAS.md, Saga 6). After the others in the list for the fights' seeds.
SIDES.push({
  id: 'lid',
  difficulty: 'heroic',
  face: 'brann',
  built: 4,
  chapters: [
    {
      id: 'neck',
      minGroup: 42,
      // A crypt's nightmares don't grow with the heroes' level the way a clearing's do: these are as tough as the group.
      toughBp: 38000,
      acts: [
        {
          kind: 'scene',
          id: 'plumb',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'brann' },
            { id: 'l3', who: 'hilde' },
            { id: 'l4', who: 'brann' },
            { id: 'l5', who: 'brann' },
            { id: 'o1', who: 'orla', if: { flag: 'lid.orla' } },
            { id: 'f1', who: 'fenn', if: { notFlag: 'lid.orla' } },
          ],
        },
        { kind: 'battle', id: 'neck', mode: 'descent', rooms: 4, place: 'neck', budgetBp: 30000, inkBp: 8000, fourBp: 9500, bestiary: { skeleton: 2, wraith: 2, gargoyle: 2, troll: 2, stalker: 2, bonesinger: 1 }, neck: true },
        { kind: 'scene', id: 'ruins', lines: [ln('l1', 'narrator'), ln('l2', 'brann')] },
        { kind: 'battle', id: 'post', mode: 'hold', land: 'quarry', place: 'post', night: true, budgetBp: 2000, inkBp: 3500, fourBp: 11000, neck: true },
        { kind: 'scene', id: 'eye', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'narrator'), ln('l4', 'brann')] },
      ],
    },
    {
      id: 'bell',
      minGroup: 45,
      acts: [
        {
          kind: 'scene',
          id: 'clapper',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'hilde' },
            { id: 'l3', who: 'hilde' },
            { id: 'u1', who: 'narrator', if: { flag: 'lid.boars' } },
            { id: 'u2', who: 'hilde', if: { flag: 'lid.boars' } },
            { id: 'n1', who: 'brann', if: { notFlag: 'lid.boars' } },
          ],
        },
        { kind: 'battle', id: 'climb', mode: 'escort', land: 'quarry', place: 'slope', budgetBp: 9000, inkBp: 3500, fourBp: 9000, cartHpBp: 16000, neck: true, climb: true },
        { kind: 'scene', id: 'frame', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'brann')] },
        { kind: 'battle', id: 'hang', mode: 'hold', land: 'quarry', place: 'post', budgetBp: 3500, inkBp: 3500, fourBp: 11000, neck: true, winch: true },
        { kind: 'scene', id: 'ring', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'narrator'), ln('l4', 'narrator'), ln('l5', 'brann')] },
      ],
    },
    {
      id: 'faces',
      minGroup: 48,
      acts: [
        { kind: 'scene', id: 'edge', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'brann'), ln('l4', 'brann')] },
        { kind: 'battle', id: 'copies', mode: 'boss', land: 'quarry', place: 'post', night: true, budgetBp: 1500, inkBp: 3500, fourBp: 11000, boss: { kind: 'corvin', tier: 3, hp: [0, 2600, 2700, 2800, 2900] }, neck: true, copies: true },
        { kind: 'scene', id: 'glass', lines: [ln('l1', 'narrator'), ln('l2', 'narrator'), ln('l3', 'brann')] },
      ],
    },
    {
      id: 'night',
      minGroup: 50,
      acts: [
        { kind: 'scene', id: 'plumb', lines: [ln('l1', 'narrator'), ln('l2', 'brann'), ln('l3', 'hilde'), ln('l4', 'brann')] },
        { kind: 'battle', id: 'dusk', mode: 'hold', land: 'quarry', night: true, budgetBp: 10000, bastion: 'dusk' },
        { kind: 'scene', id: 'midnight', lines: [ln('l1', 'narrator'), ln('l2', 'hilde'), ln('l3', 'brann')] },
        { kind: 'battle', id: 'toll', mode: 'hold', land: 'quarry', place: 'post', night: true, budgetBp: 6000, inkBp: 3500, fourBp: 11000, neck: true, toll: true },
        { kind: 'scene', id: 'dawn', lines: [ln('l1', 'narrator'), ln('l2', 'brann')] },
        { kind: 'battle', id: 'dawn', mode: 'hold', land: 'quarry', budgetBp: 10000, bastion: 'dawn' },
        {
          kind: 'scene',
          id: 'sleep',
          lines: [
            { id: 'l1', who: 'narrator' },
            { id: 'l2', who: 'narrator' },
            { id: 'hilde', who: 'hilde' },
            { id: 'odna', who: 'odna' },
            { id: 'gaspar', who: 'gaspar', if: { flag: 'lid.gaspar' } },
            { id: 'maelis', who: 'maelis', if: { flag: 'lid.maelis' } },
            { id: 'mada', who: 'mada', if: { flag: 'lid.faces' } },
            { id: 'ulla', who: 'ulla', if: { flag: 'lid.boars' } },
            { id: 'orla', who: 'orla', if: { flag: 'lid.orla' } },
            { id: 'dorn', who: 'dorn', if: { flag: 'lid.rails' } },
            { id: 'pell', who: 'pell', if: { flag: 'lid.well' } },
            { id: 'yorra', who: 'yorra', if: { flag: 'lid.shore' } },
            { id: 'fenn', who: 'fenn' },
            { id: 'brann', who: 'brann' },
          ],
        },
      ],
    },
  ],
});

export const SIDE_IDS: SideId[] = SIDES.map((x) => x.id);

/**
 * Who comes to the night of the bell (El Párpado 4, docs/SAGAS.md), in the order they come, and what brings them: the
 * Turtle done with its mark (`turtle`), a side saga done (`side`) or one of its marks (`flag`, with `side`). Each fights
 * as a hero of `cls` and comes in `from` the gate (Gaspar, on guard as always), up a road, or over the land's edge.
 */
export interface NightAlly {
  id: NpcId;
  name: string;
  cls: HeroClass;
  fight: 'dusk' | 'dawn';
  turtle?: string;
  side?: SideId;
  flag?: string;
  from: 'gate' | 'road' | 'edge';
}

export const NIGHT_ALLIES: NightAlly[] = [
  { id: 'gaspar', name: 'Ser Gaspar', cls: 'paladin', fight: 'dusk', turtle: 'gaspar.heard', from: 'gate' },
  { id: 'pell', name: 'Pell', cls: 'warrior', fight: 'dusk', side: 'well', flag: 'pozo.agua', from: 'road' },
  { id: 'maelis', name: 'Maelis', cls: 'cleric', fight: 'dusk', turtle: 'maelis.knows', from: 'road' },
  { id: 'ulla', name: 'Ulla', cls: 'barbarian', fight: 'dawn', side: 'horn', from: 'edge' },
  { id: 'dorn', name: 'Dorn', cls: 'artificer', fight: 'dawn', side: 'rails', flag: 'rails.open', from: 'road' },
  { id: 'yorra', name: 'Yorra', cls: 'ranger', fight: 'dawn', side: 'shore', from: 'edge' },
  { id: 'orla', name: 'Orla', cls: 'ranger', fight: 'dawn', side: 'burn', from: 'edge' },
];

export const SIDE = {
  /** Into the common storage for a chapter done, by difficulty (≈ half a day, a day, three, four of incursions). */
  reward: {
    light: { wood: 60, stone: 60, crystal: 30, dust: 40 },
    medium: { wood: 110, stone: 110, crystal: 60, dust: 80 },
    hard: { wood: 320, stone: 320, crystal: 180, dust: 220 },
    heroic: { wood: 420, stone: 420, crystal: 240, dust: 300 },
  } as Record<SideDifficulty, Resources>,
  /** XP for everyone who played an act of a chapter done, and for each hero in a won fight. */
  xp: 600,
  actXp: 150,
  /** A clue: how close a hero stands to it, and for how long, to pick it up (fixed units, ticks). */
  clueRange: 700,
  clueTicks: 12,
  /** A rescue: how close and how long at the pit to free one, how close they follow, how close the node counts as safe; what hurts them. */
  /**
   * The collapse: the break's first wait and how long it takes to cross from the field's edge to the firm rock (which
   * starts `rockBp` of the clearing's reach past the node, on the far side); where the scouts lie (`band`, bp of the clearing's reach
   * along the break's way, at least `apart` hexes from each other); their life, how close and how long to lift one, how
   * close and fast they follow, what hurts them; how hard the glass shoves a hero off it and burns (bp of life a second).
   */
  collapse: { first: sec(15), cross: sec(170), rockBp: 4500, band: [1500, 9500] as [number, number], apart: 2, found: [0, 0, sec(35), sec(65), sec(95), sec(125)], ahead: 1500, hp: 700, lift: 1000, ticks: sec(2), follow: 700, walk: 60, hurt: { range: 700, every: 12, damage: 40 }, shove: 60, burnBp: 600 },
  /**
   * The runaway carts: how many carts in the train (each derailed one takes its share of the train's life); its speed
   * (fixed units a tick) from the start to the top, reached after `accel`; how many times it goes round the cavern, on
   * which ring. Bone barricades: the first after `first`, then one every `every`, `ahead` points of rail before the train,
   * with `hp` life (by the nightmares' life scaling), `partyBp` more for each hero past the first. The counterweight rises when the last lap starts, with `weight` life.
   */
  rails: { carts: 4, speed: [30, 95] as [number, number], accel: sec(90), laps: 4, ring: 3, first: sec(8), every: sec(11), ahead: 9, hp: 220, weight: 600, partyBp: 6000 },
  /**
   * The alarm bells: `guards` automatons a room (and `more` for every hero past the first), their life (scaled as the
   * nightmares'), their blow (to heroes; to nightmares, scaled as the nightmares' life) every `every` within `radius`;
   * they go for what comes within `aggro` of their post and walk `step` a tick. A lever pulls after `pull` by it
   * (within `reach`); the waves come `rounds` times; won `beat` after the last lever.
   */
  /**
   * The five strikes: the first ring closes at `first`; after a strike on the beat the Anvil rests `rest`, then its ring takes `beat[0]` to close, `step` less after each
   * strike on the beat, down to `beat[1]`; a strike within `window` ticks of the close is on the beat (and opens the
   * next ring); one off it leaves that hero's hammer up for `miss`. A hero strikes within `reach` of the Anvil; the wave reaches `radius`, burns
   * `foe` (by the nightmares' life scaling) and stuns `stun`. `need` strikes win it, `beat` after; the waves come `rounds` times.
   */
  anvil: { first: sec(20), ring: [sec(6), sec(3)] as [number, number], step: sec(0.75), rest: sec(20), window: 3, miss: sec(1.5), reach: 1900, radius: 5000, foe: 120, stun: sec(2), need: 5, rounds: 3, beat: sec(3) },
  /**
   * What comes up: `towers` of the bastion's (`tower` kinds in turn, at `level`) stand round the forge, `ring` hexes out.
   * From `first`, every `every`, the boss marks the tower nearest it (within `range`); a standing hero within `guard` of it
   * before `mark` runs out saves it, else it shoots the heroes for `turn`. A blow from within `arc` (angle steps) of his
   * right side lands `weakBp`. Bruni hits at `shareBp` of his strength and never flees. Rails brought down: the
   * nightmares at `closedBp`; left open: Bruni's life at `openHpBp` and Dorn's automatons (`dorn`) at half of it, and
   * `ore` into the storage every Marea after. Bruni already fallen elsewhere: the boss is his biggest automaton, `hulk`.
   */
  /**
   * The ink in the water: `houses` round the square on ring `ring` (each `hp`), a nightmare drawn off by a hero within
   * `guard` and burning a house within `door`; the mill's stone on ring `mill`, ready from `stoneAt`, pushed by heroes
   * within `reach`, `slow` a tick alone and `fast` with two, sealing the well within `seal`. `lose` houses burned lose
   * it; won `beat` after the seal; the waves come `rounds` times.
   */
  /**
   * The Drinker: empty `empty` (quicker by `fastBp`, spitting ink `spit` at heroes out of its reach), drinking at the
   * vein `drink`, swollen `full` (slower by `slowBp`, taking `fullBp`). Burst, the water comes back after `delay`, `speed`
   * a tick up the rooms; heroes in it wade at `wadeBp` and lose `drownBp` of their life a second. Won `beat` after all are out.
   */
  /**
   * Fair day: `stalls` round the well on ring `ring` with `wares` each (stall 0 is Fenn's cart); a dropped ware is lifted
   * after `lift` within `reach`, and put back within `reach` of its stall; draggers haul at `dragBp`, `draggers` of them
   * (and `more` for each hero past the first) through its time, with `thieves` ink thieves. Lanterns light `light` round a
   * stall, `lantern` round a hero. It runs `out` (nothing new comes in its last `calm`; the thieves from `thiefAt`); `big`/`mid` of the wares back make a big or middling fair.
   */
  /**
   * The low water: the reef is the far end of one road (the causeway), the water's front starts `start` past it and
   * from `low` comes back `speed` a tick, down to `shore` (along the way out); the nightmares come `calm` later than the field's. A net left lying in the water `soak` is lost. The net comes loose after `unhook`
   * hero-ticks within `reach`; carried, `carryBp` alone (nobody within `pair`); home within `home` of the node, and held there till `dawn` (the stilt-houses, the node, with `nodeBp` of a clearing's life). In the
   * water: `wadeBp`, and `drownBp` of the life a second. The Drowned climb out every `drowned.every` with `drowned.hp` (scaled as the nightmares).
   */
  tide: { start: 1500, low: sec(45), calm: sec(40), dawn: sec(210), nodeBp: 40000, speed: 30, shore: 3200, unhook: sec(30), soak: sec(4), reach: 1000, carryBp: 6000, pair: 1500, home: 1500, wadeBp: 5500, drownBp: 1000, drowned: { every: sec(14), hp: 900 }, beat: sec(2) },
  /**
   * The fin: three lanes in from the sea, `lanes` across from the node, `half` wide each side, a post `posts` along each
   * (hold within `hold`); the first wave at `first`, then every `every` (`everyNight` at night), marked `warn` ahead;
   * a hero swept takes `hitBp` of their life and is thrown `throw` past the lane. The stilt-houses (the node) have `nodeBp` of a clearing's life. Night at `night` (the Drowned every
   * `drowned.every`, but not in the last `calm`), dawn at `dawn`.
   */
  fin: { lanes: [-2600, 0, 2600], half: 1150, posts: [-1500, 1500], hold: 700, first: sec(20), every: sec(30), everyNight: sec(16), warn: sec(5), hitBp: 2500, throw: 600, night: sec(100), dawn: sec(240), calm: sec(15), nodeBp: 40000, drowned: { every: sec(7), hp: 900 }, beat: sec(2) },
  /**
   * The Maw: its life by party size (`hp`, scaled as a saga boss: level, party), at the clearing's rim on the sea side
   * with its stalk `stalk` towards the land (`stalkHp`, scaled as the nightmares), rising at `rise`; lit, it pulls heroes within `sight`
   * `pull` a tick, `braziersBp` less with every brazier lit, and bites within `bite.range` (at least `bite.maxHpBp` of the
   * life) every `bite.every`; blows land at `litBp`, `darkBp` for `dark` after the stalk breaks. Three braziers `ring`
   * hexes from the lighthouse (the node); put out after `douse` with a Drowned by one (within `reach`) and no hero, lit
   * after `relight`. A Drowned every `drowned.every`. The lighthouse has `nodeBp` of a clearing node's life.
   */
  maw: { hp: [0, 4200, 7000, 11000, 16000], stalk: 2200, stalkHp: 300, rise: sec(5), sight: 3600, pull: 45, braziersBp: 6000, litBp: 2500, darkBp: 25000, dark: sec(10), bite: { range: 1300, every: sec(1.5), damage: 90, maxHpBp: 1500 }, braziers: 3, ring: 2, reach: 900, douse: sec(4), relight: sec(2), nodeBp: 60000, drowned: { every: sec(15), hp: 900 }, beat: sec(3) },
  /**
   * The way back from the bones: the sea comes in `delay` after the start where they came down, `speed` a tick, through
   * the rooms to the last one; in it `wadeBp`, `drownBp` of the life a second. The heart (if taken) slows its carrier to
   * `carryBp`, is lifted within `reach`, and is lost after `soak` lying in the water. A Drowned at the water's edge every `drowned.every`, once the water is `drowned.after` in.
   */
  flight: { delay: sec(3), speed: 100, wadeBp: 5500, drownBp: 1000, heart: { reach: 900, carryBp: 7800, soak: sec(3) }, drowned: { every: sec(4), hp: 300, after: 5000 }, beat: sec(2) },
  /**
   * The dragon in the water: fire down a lane (the fin's lanes and posts, SIDE.fin), first at `first`, then every
   * `every` (`everyNight` once the Drowned come), marked `warn` ahead; a hero in it takes `hitBp` of their life (a post
   * doesn't help against fire). The Drowned from `night`, `drowned.batch` at a time (by party size). Dawn at `dawn`, `heartDawn` with the
   * heart, whose carrier the fire follows; it slows them to `heart.carryBp` and is lifted within `heart.reach`.
   */
  breath: { first: sec(15), every: sec(20), everyNight: sec(14), warn: sec(3), hitBp: 3000, night: sec(50), dawn: sec(200), heartDawn: sec(250), calm: sec(15), nodeBp: 40000, drowned: { every: sec(18), hp: 900, batch: [0, 1, 2, 3, 4] }, heart: { reach: 900, carryBp: 7800 }, beat: sec(2) },
  /**
   * The Drowned Dragon: its life by party size (`hp`, scaled as a saga boss: level, party; `heartBp` more with the heart
   * gone from its chest), rising at the clearing's rim on the sea side at `rise`. Blows land at `takenBp`; the fin
   * crushing it takes `crushBp` of its whole life. Every `breath.every` it draws breath for `breath.draw` (still) and
   * breathes down a band `breath.half` wide and `breath.range` long, `breath.hitBp` of a hero's life. Yorra's boats
   * (`boats` of them on the sea side) harpoon it every `harpoon.every` from `harpoon.first`, holding it `harpoon.hold`.
   * The fin: its wave first at `fin.first`, every `fin.every`, marked `fin.warn` ahead. The Drowned every
   * `drowned.every`. The stilt-houses have `nodeBp` of a clearing node's life.
   */
  sunken: {
    hp: [0, 9000, 14000, 22000, 32000],
    heartBp: 13000,
    rise: sec(4),
    takenBp: 3500,
    crushBp: 3334,
    breath: { every: sec(9), draw: sec(1.5), half: 900, range: 5000, hitBp: 2200 },
    boats: 3,
    harpoon: { first: sec(14), every: sec(19), hold: sec(5) },
    // No dawn tonight: it ends with the dragon (`dawn` only keeps the fin's own night going).
    fin: { first: sec(25), every: sec(22), warn: sec(6), dawn: sec(3600) },
    drowned: { every: sec(12), hp: 900 },
    nodeBp: 60000,
    beat: sec(3),
  },
  /**
   * The titan's neck: its breath, `shut` in (the folds closed) and `open` out; a hero in a fold as it shuts takes `squeezeBp`
   * of their life and is put back in the room behind. It shakes first at `shake.first`, then every `shake.every`, the dust
   * jumping `shake.warn` ahead; a hero walking when it hits falls for `shake.down` and takes `shake.hitBp` of their life. The Order's post (the
   * field's node) has `postBp` of a clearing node's life: its old walls still stand.
   */
  neck: { open: sec(5), shut: sec(6), squeezeBp: 1500, postBp: 30000, shake: { first: sec(18), every: sec(30), warn: sec(2.5), down: sec(2), hitBp: 800 } },
  /**
   * The bell's climb: `ropes` on it (each cut by its share of the bell's life in blows), dragged at `pace[n]` (fixed
   * units a tick) by `n` heroes within `pull` of it (Ulla's boars pull as `boars` more); a rope cut slides it back `slip`
   * points of its road at `slide`, a shake `shakeSlip` unless a hero within `brace` of it stands still.
   */
  climb: { ropes: 4, pull: 1900, pace: [0, 14, 22, 27, 31, 34], boars: 2, slip: 3, shakeSlip: 2, slide: 150, brace: 1500 },
  /**
   * Hanging the bell: `winches` round the frame, `ring` hexes out, between the roads; a hero within `reach` turns one. It
   * takes `need` turns of one winch for each hero (up to the winches), and sinks back `sink` a tick with none turned. The
   * waves come round `rounds` times. The frame is the Order's post: its life is SIDE.neck.postBp.
   */
  winch: { winches: 3, ring: 3, reach: 900, need: sec(75), sink: 3, rounds: 4, beat: sec(3) },
  /**
   * The copies: the share of all an Unsleeping does that they hit with (× the party's level, as the Prestacaras), how much
   * harder a hero hits their own, the wait before the first and between one falling and the next, how long an outing
   * lasts at most, the moment after the last one falls.
   */
  copies: { dmgBp: 5000, ownBp: 20000, first: sec(6), gap: sec(5), out: sec(480), beat: sec(3) },
  /**
   * The night of the bell at the bastion: the last wave of the group's Marea, its nightmares × `budgetBp` at dusk (with
   * the Hollow Shell at `shellBp` of its life, and the Ash Witch at `witchBp` of hers if the Turtle didn't redeem her) and
   * × `dawnBp` at dawn (no boss). The wave is already the bastion's measure: bigger, a group playing it live loses it. The allies: the next one comes once the keep is under `hurtBp[i]` of its life or at `at[i]`, whichever is
   * first; their way in hits the nightmares within `blow.r` (`blow.foe`, × their toughness) and stuns them `blow.stun`.
   * They fight at `powerBp`. The ones over the land's edge come in `inset` hexes short of it (the edge is the fog bank:
   * their way in would go unseen). At dawn, the ones who came at dusk are there from the start.
   */
  night: { budgetBp: 10000, dawnBp: 10000, shellBp: 6000, witchBp: 8000, at: [sec(40), sec(80), sec(120), sec(160)], hurtBp: [8000, 6000, 4000, 2500], blow: { r: 3500, foe: 400, stun: sec(2) }, powerBp: 9000, inset: 3 },
  /**
   * The bell at midnight: the first ring closes at `first`; each takes `ring[0]` to close, `step` less for each blow on
   * the beat, never under `ring[1]`; `window` ticks either side of the close count. A hero within `reach` of the bell
   * strikes; off the beat the hammer is up `miss` and the eyelid trembles (the neck's shake, at once). On the beat every
   * nightmare within `radius` is pushed `push` away and stunned `stun`, and the calm goes up one. `need` of calm wins
   * it; the field's waves come round `rounds` times.
   */
  toll: { first: sec(12), ring: [sec(6), sec(3.5)] as [number, number], step: sec(0.25), window: 3, miss: sec(1.5), reach: 1900, radius: 6000, push: 1500, stun: sec(1.5), need: 20, rounds: 4, beat: sec(3) },
  /** The Drowned's grip: it drags a hero at `dragBp` of its pace, `hold` behind it; `cutBp` of its life in blows frees them; then `again` before it can grab again. */
  drowned: { dragBp: 7000, hold: 500, cutBp: 2500, again: sec(3) },
  fair: { stalls: 6, ring: 3, wares: 2, reach: 800, lift: sec(1), dragBp: 7000, draggers: 14, more: 5, thieves: 6, light: 1200, lantern: 1100, out: sec(150), calm: sec(20), thiefAt: sec(15), big: 7500, mid: 4000, beat: sec(2) },
  drinker: { empty: sec(9), drink: sec(4), full: sec(8), fastBp: 13000, slowBp: 5000, fullBp: 20000, spit: { range: 4500, every: sec(1.6), dmgBp: 8000 }, flood: { delay: sec(2.5), speed: 105, wadeBp: 5500, drownBp: 1200 }, beat: sec(2) },
  well: { houses: 8, ring: 4, hp: 300, guard: 1400, door: 700, mill: 6, stoneAt: sec(75), reach: 900, slow: 15, fast: 40, seal: 800, lose: 4, rounds: 3, beat: sec(3) },
  rising: {
    towers: 4,
    tower: ['ballista', 'arcane'] as const,
    level: 1 as const,
    material: 1 as const,
    ring: 2,
    first: sec(12),
    every: sec(8),
    mark: sec(3),
    guard: 1000,
    turn: sec(5),
    range: 9000,
    arc: 200,
    weakBp: 20000,
    shareBp: 10000,
    closedBp: 7500,
    openHpBp: 12000,
    dorn: { n: 2, more: 1, hp: 2500, damage: 0, foe: 90, every: sec(1.5), radius: 800, aggro: 9000, taunt: 3000, step: 75 },
    ore: { wood: 0, stone: 120, crystal: 40, dust: 0 } as Resources,
  },
  bells: { rooms: 3, guards: 2, more: 1, hp: 900, damage: 170, foe: 30, every: sec(1.5), radius: 800, aggro: 1800, taunt: 5000, step: 70, pull: sec(8), reach: 900, rounds: 3, beat: sec(3) },
  /**
   * The old trap: how close a hero stands to a winch or the lever to work it, how long each winch takes, the wait between
   * the pull and the shot; where the line starts (closer, the harpoon flies over), its width (to each side) and reach (bp of the clearing's); what a harpoon takes off the
   * boss (bp of its life) and does to anything else; what the boss takes of every other blow (bp); the winches stand this
   * far to each side of the engine, the lever this far past the line's edge halfway down it; the day out there lasts `out`.
   */
  trap: { reach: 900, wind: sec(4), delay: 10, near: 2000, half: 800, lenBp: 21000, hitBp: 4000, foe: 2000, shieldBp: 2000, winchOff: 1700, leverOff: 900, out: sec(420) },
  /**
   * The blasting: how close to stand to lift a barrel (and how long), how close to a crack to plant it, its fuse and the
   * blows it takes before it goes off by itself; a blast's reach and what it does (to the nightmares, and bp of a hero's
   * life); the beat between the beacon lit and the win. The fight's waves come `rounds` times over while cracks are open.
   * The first barrel comes down at `first`, then one `refill` after the last leaves the stack; a crack takes `charges` blasts.
   */
  blast: { reach: 900, pick: sec(1), plant: 1000, fuse: sec(3), hp: 260, radius: 1800, foe: 900, heroBp: 2500, beat: sec(3), rounds: 3, first: sec(10), refill: sec(12), charges: 2 },
  rescue: { free: 1100, ticks: 24, follow: 700, speed: 60, home: 1800, hurt: { range: 700, every: 12, damage: 40 }, seen: 2200 },
  /**
   * The Prestacaras: its life against the party (× the saga boss's, see SideBattle.facetaker), the share of an
   * Unsleeping's blows it hits with, the faces it changes at (life left, bp), how long its face slips near a lantern (its
   * own feet in the light, or a hero within `fightRange` of it standing in the light)
   * and the wait before it can slip again, how much harder it is hit meanwhile; a start in a rush; the lanterns.
   */
  facetaker: { dmgBp: 5000, swapAtBp: [7500, 5000, 2500], bareTicks: sec(6), bareCd: sec(5), fightRange: 6000, bareTakenBp: 15000, holdTicks: sec(5), rushTicks: sec(10), rushBp: 14000, lanternR: 1300, lanterns: { right: 4, wrong: 2 }, wrongHpBp: 13000 },
  /** The night of lanterns: how far a hero's lantern lights, how close and how long to tear a face off. */
  crowd: { lantern: 2600, unmask: 1000, ticks: 12 },
  /**
   * The drive: how close a hero scares a boar off (and how fast it goes), how close a hero draws a nightmare off the
   * herd, how it drifts back to the herd, how close a
   * nightmare makes it bolt (and for how long, how fast); its life and what hurts it; a boar alone this long brings
   * wolves; an ogre's roar sends the herd running (how long, how fast; what it does to the nightmares it runs over).
   * Where it starts and the pen lie this many hexes from the node; the pen this wide. Each boar past `need` pays this.
   */
  herd: {
    scare: 1700,
    guard: 2400,
    speed: 105,
    drift: 25,
    flock: 1300,
    apart: 420,
    panic: { range: 1000, ticks: sec(2), speed: 150 },
    hp: 700,
    hurt: { range: 650, every: 12, damage: 30 },
    lone: { range: 2400, ticks: sec(8), wolves: 2, cd: sec(25) },
    stampede: { reach: 5000, ticks: sec(3), speed: 230, hit: 650, damage: 140, stun: sec(2), cd: sec(20) },
    ring: 7,
    penR: 1300,
    extra: { wood: 8, stone: 12 } as Partial<Resources>,
  },
  /**
   * The lash: the warning before the ground tilts, how long it slides and how far (the big one, this much more; the
   * nightmares, which don't know how to hold on, this much more again); how
   * close to a mooring post a hero holds on; the barrels (how close they crush, how fast and long they roll, what they
   * do); how many hexes out the posts stand (close to the camp: holding on and holding the camp go together), and the barrels.
   */
  lash: {
    warn: sec(3),
    ticks: 10,
    slide: 2400,
    bigBp: 20000,
    foeBp: 20000,
    postR: 950,
    barrel: { r: 520, speed: 200, ticks: 24, damage: 220, stun: sec(1) },
    posts: 2,
    ring: [3, 4] as [number, number],
  },
  /**
   * The hunt: the Uro heals `healBp` of its whole life a day; a week with nobody after it (`roamDays`), it moves to
   * another of its `places` (this many hexes from the camp) with its wounds halved and the traps left behind. It grazes
   * within `roam` of its place, at `walk` a tick; sees what moves within `sight` (a hero unmoved for `still` ticks is
   * invisible to it); paws the ground `wind` ticks and charges (`charge`: how fast and long, how close it hits, what it
   * does to a hero and to a nightmare, how far it throws a hero; it pulls up `brake` short of a hero who went still,
   * and stands blowing `rest` ticks after). Traps: `per` hero an outing, `max` laid at once; within `r` it is caught
   * (`hold` ticks, `damage`). Back at the camp (within `camp` of the node) the group may end the outing. Its whole
   * life: the chapter's boss `hp` by the group's active heroes (not the outing's), grown with their level as a saga boss's.
   */
  hunt: {
    healBp: 1500,
    roamDays: 7,
    places: 3,
    ring: 6,
    roam: 1600,
    walk: 30,
    sight: 4200,
    still: 8,
    wind: sec(1),
    charge: { speed: 260, ticks: sec(2.5), hit: 650, hero: 160, foe: 260, shove: 120, brake: 1300, rest: sec(2.5) },
    trap: { per: 2, max: 8, r: 520, hold: sec(5), damage: 700 },
    camp: 1800,
  },
  /**
   * Ragna in the graveyard: the share of all she does to the heroes (her blows grow with the group's level as in a wave);
   * with the clan's call, the share of her life she kneels at and the moment before the fight ends (nothing moves).
   */
  ragna: { dmgBp: 5000, callBp: 5000, kneel: sec(3) },
  /** Where clues lie: this many hexes from the node, off the roads. */
  clueRing: [3, 5] as [number, number],
};
